import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { useCampaignRealtime } from '#/entities/campaign'

import { CAMPAIGN_DETAIL_DEFAULT_TAB } from '../model/detail-tabs'
import { AccessDenied } from '#/shared/ui/access-denied'
import { Button } from '#/shared/ui/button'
import { DataTable } from '#/shared/ui/data-table'
import { Toolbar } from '#/shared/ui/toolbar'

import { useCampaignDocumentTitle } from '../model/use-campaign-document-title'
import { useCampaignPage } from '../model/use-campaign-page'
import { useCampaignRowActions } from '../model/use-campaign-row-actions'
import { useCampaignColumns } from './campaign-columns'
import { CampaignDeleteDialog } from './campaign-delete-dialog'
import { CampaignStatusTabs } from './campaign-status-tabs'
import { CampaignSummaryStrip } from './campaign-summary-strip'
import { CampaignToolbar } from './campaign-toolbar'
import { NewCampaignDialog } from './new-campaign-dialog'

function CampaignNoAccess() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col md:min-h-0 md:flex-1">
      <AccessDenied
        title={t('campaigns.noAccess.title')}
        description={t('campaigns.noAccess.description')}
        action={
          <Button asChild variant="outline" size="sm" pressEffect>
            <Link to="/">{t('forbidden.goHome')}</Link>
          </Button>
        }
      />
    </div>
  )
}

export function CampaignPage() {
  useCampaignDocumentTitle()
  useCampaignRealtime()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)
  const {
    isForbidden,
    rows,
    isLoading,
    summary,
    isSummaryLoading,
    tabs,
    activeTab,
    onTabChange,
    search,
    onSearchChange,
    selectedIds,
    onSelectionChange,
    rangeFrom,
    rangeTo,
    total,
    onRangeChange,
    pagination,
  } = useCampaignPage()
  const { actions, deleteTarget, onDeleteCancel, onDeleteConfirm, isDeleting } =
    useCampaignRowActions()
  const columns = useCampaignColumns(actions)

  if (isForbidden) return <CampaignNoAccess />

  return (
    <div className="flex flex-col gap-4 md:min-h-0 md:flex-1">
      <CampaignSummaryStrip tiles={summary} loading={isSummaryLoading} />
      <Toolbar>
        <CampaignStatusTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
        <CampaignToolbar
          search={search}
          onSearchChange={onSearchChange}
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          total={total}
          onRangeChange={onRangeChange}
          onNewCampaign={() => setCreateOpen(true)}
        />
      </Toolbar>
      <DataTable
        storageKey="campaign-table-pins"
        className="[&_thead_th]:text-gray-500 [&_thead_tr]:bg-gray-50"
        columns={columns}
        data={rows}
        isLoading={isLoading}
        selectable
        onRowClick={(row) =>
          void navigate({
            to: '/campaign/$campaignId',
            params: { campaignId: row.id },
            search: { tab: CAMPAIGN_DETAIL_DEFAULT_TAB },
          })
        }
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        emptyVariant={search === '' ? 'initial' : 'filtered'}
        pagination={pagination}
        rowHeight="h-16.5"
        minWidth={1000}
        fillHeight
      />
      <NewCampaignDialog open={createOpen} onOpenChange={setCreateOpen} />
      <CampaignDeleteDialog
        campaign={deleteTarget}
        busy={isDeleting}
        onConfirm={onDeleteConfirm}
        onCancel={onDeleteCancel}
      />
    </div>
  )
}
